import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../interceptors";

// Backend serializes List<String> by JSON.stringify-ing the array once and
// then splitting it on commas, so a 3-item list arrives as fragments like
//   ["[\"HR\"", "\"Manager\"", "\"Tech\"]"]
// (or even out-of-order — `"\"HR\"]"`, `"\"Manager\""`, `"[\"Tech\""`).
// Strategy: join → JSON.parse first; fall back to literal-strip per element
// when the joined string is no longer valid JSON. Same fix as parseStringList
// in screens/admin/jobs/jobs.jsx — worth extracting to a shared util if a
// third caller appears.
const parseStringList = (val) => {
  if (val == null) return [];
  if (!Array.isArray(val)) {
    if (typeof val === "string" && val) return [val];
    return [];
  }
  if (val.length === 0) return [];
  try {
    const parsed = JSON.parse(val.join(","));
    if (Array.isArray(parsed))
      return parsed.filter((s) => s != null && s !== "");
    if (typeof parsed === "string") return parsed ? [parsed] : [];
    return [];
  } catch {
    return val
      .map((s) => String(s).replace(/^\s*\[?"?|"?\]?\s*$/g, ""))
      .filter(Boolean);
  }
};

export const fetchRoundsForJob = createAsyncThunk(
  "questionBank/fetchRoundsForJob",
  async ({ jobId }, { rejectWithValue }) => {
    try {
      // Uses /interview-question/rounds-for-job which reads from
      // amplehire_job_details_interview_rounds_t (the table the save flow needs).
      // The /define-interviews/get-all endpoint reads a DIFFERENT table that's
      // often empty for jobs created outside the legacy define-interview flow.
      const r = await axiosInstance.post(
        "/job-posting/interview-question/rounds-for-job",
        { jobId: String(jobId) },
      );
      if (!r.data || r.data.status === false) {
        return rejectWithValue(r.data?.message || "Failed to load rounds");
      }
      return { jobId, rounds: parseStringList(r.data.rounds) };
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to load rounds");
    }
  },
);

export const fetchQuestions = createAsyncThunk(
  "questionBank/fetchQuestions",
  async (
    { jobId, interviewRound, filters } = {},
    { rejectWithValue },
  ) => {
    try {
      // Backend uses @RequestBody Map<String,String> — JSON, not multipart.
      const body = {};
      if (jobId != null) body.jobId = String(jobId);
      if (interviewRound) body.interviewRound = interviewRound;
      if (filters?.types?.length) body.questionTypes = filters.types.join(",");
      if (filters?.difficulties?.length) body.difficulties = filters.difficulties.join(",");
      if (filters?.languages?.length) body.languages = filters.languages.join(",");
      if (filters?.tags?.length) body.tagsJson = JSON.stringify(filters.tags);
      if (filters?.search) body.search = filters.search;

      const r = await axiosInstance.post(
        "/job-posting/interview-question/get-all",
        body,
      );
      if (!r.data || r.data.status === false) {
        return rejectWithValue(r.data?.message || "Failed to load questions");
      }
      return r.data.questions || [];
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to load questions");
    }
  },
);

// Hybrid-scope save: covers the common fields. The advanced fields (probing
// questions, per-language translations, enhanced media upload) are intentionally
// not collected here — power users click "Switch to advanced editor" which opens
// the legacy /admin/add-questions screen.
export const saveQuestion = createAsyncThunk(
  "questionBank/saveQuestion",
  async ({ jobId, interviewRound, question }, { rejectWithValue }) => {
    try {
      const fd = new FormData();
      fd.append("jobId", String(jobId));
      fd.append("interviewRound", interviewRound);
      if (question.questionId != null) {
        fd.append("questionId", String(question.questionId));
      }
      fd.append("questionText", question.questionText || "");
      fd.append("questionType", question.questionType || "VIDEO");
      fd.append("responseType", question.responseType || "video");
      fd.append("competancy", question.competency || "");
      fd.append("defaultLanguage", question.defaultLanguage || "en");
      fd.append("difficulty", question.difficulty || "MEDIUM");
      fd.append(
        "responseTimeInMinutes",
        String(Number(question.responseTimeInMinutes) || 0),
      );
      fd.append(
        "responseTimeInSeconds",
        String(Number(question.responseTimeInSeconds) || 0),
      );
      // Tags: send as JSON string. Backend DTO has List<String> tags;
      // session 8 confirms multipart binding works or adds a tagsJson setter.
      fd.append("tagsJson", JSON.stringify(question.tags || []));
      fd.append(
        "questionOptions",
        JSON.stringify(question.questionOptions || []),
      );
      fd.append("correctOption", question.correctOption || "");
      fd.append("lipSyncVideoLink", question.lipSyncVideoLink || "");
      fd.append(
        "terminateInterview",
        String(Boolean(question.terminateInterview)),
      );
      fd.append(
        "isMoveToQuestionBank",
        String(Boolean(question.isMoveToQuestionBank)),
      );
      fd.append("isPractice", String(Boolean(question.isPractice)));
      // Feature #2: type-specific metadata (CODING/SKILL config). JSON string.
      // Empty/null for VIDEO/MCQ/BEHAVIORAL/LIP_SYNC/PRACTICE.
      // DB column is JSONB; empty string is invalid JSON and the insert
      // fails with "input string ended unexpectedly". Default to "{}".
      const tm = question.typeMetadata;
      let tmSerialized;
      if (tm == null) {
        tmSerialized = "{}";
      } else if (typeof tm === "string") {
        tmSerialized = tm.trim() === "" ? "{}" : tm;
      } else {
        tmSerialized = JSON.stringify(tm);
      }
      fd.append("typeMetadata", tmSerialized);
      // Defaults for fields the hybrid form does NOT collect — required by the
      // legacy save endpoint's DTO.
      fd.append("isEnhanced", "false");
      fd.append("isProbeQuestion", "false");
      fd.append("probingQuestions", "[]");
      fd.append("questionBankTranslations", "[]");

      const endpoint =
        question.questionId != null
          ? "/job-posting/interview-question/update"
          : "/job-posting/interview-question/save";

      const r = await axiosInstance.post(endpoint, fd, {
        headers: { "Content-type": "multipart/form-data" },
      });
      if (!r.data || r.data.status === false) {
        return rejectWithValue(r.data?.message || "Failed to save question");
      }
      return r.data;
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to save question");
    }
  },
);

export const reorderQuestion = createAsyncThunk(
  "questionBank/reorderQuestion",
  async ({ interviewId, questionId, newOrder }, { rejectWithValue }) => {
    try {
      const r = await axiosInstance.patch(
        "/job-posting/interview-question/mapping/order",
        { interviewId, questionId, newOrder },
      );
      if (!r.data || r.data.status === false) {
        return rejectWithValue(r.data?.message || "Failed to reorder");
      }
      return r.data;
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to reorder");
    }
  },
);

export const fetchAvailableTags = createAsyncThunk(
  "questionBank/fetchAvailableTags",
  async (_arg, { rejectWithValue }) => {
    try {
      const r = await axiosInstance.get("/job-posting/interview-question/tags");
      if (!r.data || r.data.status === false) {
        return rejectWithValue(r.data?.message || "Failed to load tags");
      }
      return r.data.tags || [];
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to load tags");
    }
  },
);

export const generateQuestions = createAsyncThunk(
  "questionBank/generateQuestions",
  async (
    { jobTitle, jobId, count, questionType, competency, difficulty, language },
    { rejectWithValue },
  ) => {
    try {
      // QuestionGenerationService synchronously calls Anthropic; a 5-question
      // batch can land in the 15-30s range and tail past 30s for larger
      // counts. Override axios' default timeout so the request rides out the
      // model latency instead of failing in the UI while the backend keeps
      // working.
      const r = await axiosInstance.post(
        "/ai/questions/generate",
        {
          jobTitle,
          jobId,
          count,
          questionType,
          competency,
          difficulty,
          language,
        },
        { timeout: 60000 },
      );
      if (!r.data || !Array.isArray(r.data.questions)) {
        return rejectWithValue("Generator did not return any questions");
      }
      return r.data.questions;
    } catch (e) {
      if (e?.code === "ECONNABORTED") {
        return rejectWithValue("Generator timed out after 60s — try a smaller count");
      }
      return rejectWithValue(e?.message || "Failed to generate questions");
    }
  },
);

const emptyFilters = () => ({
  search: "",
  types: [],
  difficulties: [],
  tags: [],
  skillIds: [],
  languages: [],
});

// Shared sentinel so selectors don't create a fresh [] on each render,
// which would otherwise trigger unnecessary re-renders in useSelector consumers.
const EMPTY_ARRAY = Object.freeze([]);

const questionBankSlice = createSlice({
  name: "questionBank",
  initialState: {
    items: [],
    status: "idle", // idle | loading | succeeded | failed
    error: null,
    saving: false,
    saveError: null,
    roundsByJob: {}, // { [jobId]: string[] }
    roundsStatus: "idle",
    generating: false,
    generateError: null,
    lastGenerated: [],
    reordering: false,
    reorderError: null,
    availableTags: [],
    filters: emptyFilters(),
  },
  reducers: {
    setFilter(state, { payload: { key, value } }) {
      state.filters[key] = value;
    },
    setAllFilters(state, { payload }) {
      state.filters = { ...emptyFilters(), ...(payload || {}) };
    },
    clearFilters(state) {
      state.filters = emptyFilters();
    },
    clearLastGenerated(state) {
      state.lastGenerated = [];
      state.generateError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchQuestions.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchQuestions.fulfilled, (state, { payload }) => {
        state.items = payload;
        state.status = "succeeded";
      })
      .addCase(fetchQuestions.rejected, (state, { payload }) => {
        state.status = "failed";
        state.error = payload || "Failed to load questions";
      })
      .addCase(saveQuestion.pending, (state) => {
        state.saving = true;
        state.saveError = null;
      })
      .addCase(saveQuestion.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(saveQuestion.rejected, (state, { payload }) => {
        state.saving = false;
        state.saveError = payload || "Failed to save question";
      })
      .addCase(fetchRoundsForJob.pending, (state) => {
        state.roundsStatus = "loading";
      })
      .addCase(fetchRoundsForJob.fulfilled, (state, { payload }) => {
        state.roundsByJob[payload.jobId] = payload.rounds;
        state.roundsStatus = "succeeded";
      })
      .addCase(fetchRoundsForJob.rejected, (state) => {
        state.roundsStatus = "failed";
      })
      .addCase(reorderQuestion.pending, (state) => {
        state.reordering = true;
        state.reorderError = null;
      })
      .addCase(reorderQuestion.fulfilled, (state) => {
        state.reordering = false;
      })
      .addCase(reorderQuestion.rejected, (state, { payload }) => {
        state.reordering = false;
        state.reorderError = payload || "Failed to reorder";
      })
      .addCase(generateQuestions.pending, (state) => {
        state.generating = true;
        state.generateError = null;
        state.lastGenerated = [];
      })
      .addCase(generateQuestions.fulfilled, (state, { payload }) => {
        state.generating = false;
        state.lastGenerated = payload;
      })
      .addCase(generateQuestions.rejected, (state, { payload }) => {
        state.generating = false;
        state.generateError = payload || "Failed to generate questions";
      })
      .addCase(fetchAvailableTags.fulfilled, (state, { payload }) => {
        state.availableTags = payload;
      });
  },
});

export const { setFilter, setAllFilters, clearFilters, clearLastGenerated } = questionBankSlice.actions;

export const selectQuestions = (s) => s.questionBankSliceReducer.items;
export const selectQuestionsStatus = (s) => s.questionBankSliceReducer.status;
export const selectQuestionsError = (s) => s.questionBankSliceReducer.error;
export const selectQuestionFilters = (s) => s.questionBankSliceReducer.filters;
export const selectIsSavingQuestion = (s) => s.questionBankSliceReducer.saving;
export const selectSaveQuestionError = (s) => s.questionBankSliceReducer.saveError;
export const selectRoundsForJob = (jobId) => (s) =>
  s.questionBankSliceReducer.roundsByJob[jobId] || EMPTY_ARRAY;
export const selectIsGenerating = (s) => s.questionBankSliceReducer.generating;
export const selectGenerateError = (s) => s.questionBankSliceReducer.generateError;
export const selectLastGenerated = (s) => s.questionBankSliceReducer.lastGenerated;
export const selectReorderError = (s) => s.questionBankSliceReducer.reorderError;
export const selectAvailableTags = (s) => s.questionBankSliceReducer.availableTags;

export default questionBankSlice.reducer;
